// src/views/proprio/ProprioIncidentBits.jsx — briques d'affichage incident proprio.
//
// Mutualisé (zéro duplication) entre la « vue d'ensemble » (liste compacte) et
// le DÉPLI d'un passage ménage (incident complet inline) — fusion onglet
// « Ménage & incidents ». Lecture seule. Notes internes FBZ jamais affichées
// (`cout` strippé côté serveur ; whitelist INCIDENT_COLS). Escaping React natif.

import { Coins, AlertTriangle, ChevronRight } from 'lucide-react'
import { DS } from '../../lib/ui'
import { fmtDate, fmtE } from '../../lib/format'
import { incAircoverTotal } from '../../lib/incidents-helpers'

// Statuts au format sémantique DA (warning / danger / success).
export const INCIDENT_STATUS_META = {
  en_cours: { label: 'En cours', color: DS.warningText },
  a_racheter: { label: 'À racheter', color: DS.dangerText },
  resolu: { label: 'Résolu', color: DS.successText },
}

// Couleur du statut ménage par dégradation (libellés métier ménage) — tokens DA.
export const STATUT_MENAGE_COLOR = {
  'À traiter': DS.warningText,
  'À vérifier': DS.warningText,
  'Récupérable': DS.marine,
  'Non récupérable': DS.dangerText,
  'À racheter': DS.dangerText,
  'Remplacé': DS.successText,
}

function statusMeta(inc) {
  return INCIDENT_STATUS_META[inc.status] || { label: inc.status || '—', color: DS.textMuted }
}

// Bandeau récap remboursements AirCover / caution.
export function RefundBanner({ total, n, style }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', background: DS.successBg, border: `0.5px solid ${DS.success}44`, borderRadius: DS.radiusCard, padding: '12px 14px', ...style }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, fontSize: 14 }}>
        <Coins size={16} strokeWidth={1.8} color={DS.success} /> Remboursements AirCover / caution
      </span>
      <span style={{ fontSize: 14 }}>
        <span style={{ color: DS.success, fontWeight: 700 }}>{fmtE(total)}</span>
        <span style={{ fontSize: 12, color: DS.textMuted }}> · {n} incident{n > 1 ? 's' : ''}</span>
      </span>
    </div>
  )
}

// Ligne compacte d'un incident (vue d'ensemble). Cliquable si onClick fourni
// (→ déplie le passage ménage lié et scrolle dessus).
export function IncidentSummaryRow({ inc, onClick }) {
  const meta = statusMeta(inc)
  const refund = incAircoverTotal(inc)
  const clickable = typeof onClick === 'function'
  return (
    <button type="button" onClick={onClick} disabled={!clickable} className={clickable ? 'fbz-rowhover' : undefined}
      style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderTop: `1px solid ${DS.border}`, background: 'none', cursor: clickable ? 'pointer' : 'default', padding: '9px 4px', fontFamily: DS.fontBody }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: DS.marine, fontWeight: 600 }}>
          {inc.label || 'Incident'}
          <span style={{ color: DS.textMuted, fontWeight: 400 }}>
            {' · '}{inc.incident_date ? fmtDate(inc.incident_date) : fmtDate(inc.reported_at)}
            {inc.voyageur ? ` · ${inc.voyageur}` : ''}
          </span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {refund > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: DS.success, fontWeight: 700 }}><Coins size={12} strokeWidth={1.8} /> {fmtE(refund)}</span>}
          <span style={{ background: meta.color + '22', color: meta.color, borderRadius: 7, padding: '2px 9px', fontSize: 10.5, fontWeight: 600 }}>{meta.label}</span>
          {clickable && <ChevronRight size={14} strokeWidth={1.8} color={DS.textMuted} />}
        </span>
      </div>
    </button>
  )
}

// Incident COMPLET inline (dépli d'un passage) : statut + dégradations (type +
// statut ménage) + photos de constat + remboursement greffé. PAS de coût de rachat.
export function IncidentFull({ inc }) {
  const meta = statusMeta(inc)
  const degs = Array.isArray(inc.degradations) ? inc.degradations : []
  const refund = incAircoverTotal(inc)
  const photos = Array.isArray(inc.photos) ? inc.photos : []
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: DS.dangerBg, borderRadius: DS.radiusControl, border: `0.5px solid ${DS.danger}33` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700, color: DS.dangerText, fontSize: 12.5 }}><AlertTriangle size={14} strokeWidth={1.8} /> {inc.label || 'Incident'}</span>
        <span style={{ background: meta.color + '22', color: meta.color, borderRadius: 7, padding: '2px 9px', fontSize: 10.5, fontWeight: 600 }}>{meta.label}</span>
      </div>
      {inc.description && <div style={{ fontSize: 12.5, color: DS.textPrimary, marginTop: 4, whiteSpace: 'pre-wrap' }}>{inc.description}</div>}

      {degs.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {degs.map((d, i) => {
            const sc = STATUT_MENAGE_COLOR[d.statutMenage] || DS.textMuted
            return (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: DS.card, borderRadius: 7, padding: '2px 8px', fontSize: 11, border: `0.5px solid ${DS.danger}26` }}>
                <span style={{ color: DS.textSecondary, fontWeight: 600 }}>{d.type || d.label || 'dégradation'}</span>
                {d.statutMenage && <span style={{ color: sc, fontWeight: 700 }}>· {d.statutMenage}</span>}
              </span>
            )
          })}
        </div>
      )}

      {photos.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          {photos.map((p, i) => (
            <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'block', width: 72, height: 72, borderRadius: 8, overflow: 'hidden', border: `0.5px solid ${DS.border}` }}>
              <img src={p.url} alt={p.fileName || `constat ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </a>
          ))}
        </div>
      )}

      {refund > 0 && (
        <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, background: DS.successBg, color: DS.successText, borderRadius: DS.radiusControl, padding: '5px 10px', fontSize: 12, fontWeight: 700, border: `0.5px solid ${DS.success}44` }}>
          <Coins size={14} strokeWidth={1.8} /> Remboursé : {fmtE(refund)}
        </div>
      )}
    </div>
  )
}
